import React, {useLayoutEffect, useRef} from 'react';
import {Link} from 'react-router-dom';
import {AppleFilled, AndroidFilled, CheckCircleOutlined} from '@ant-design/icons';
import {APP_STORE_URL, GOOGLE_PLAY_URL, BIZ_URL} from '../../content/appLinks';
import './LandingPage.css';

/**
 * Landing aplikacji klienckiej MyBeautyPlace.
 *
 * Strona jest wyłącznie wizytówką: nie ma logowania ani rejestracji, bo całość
 * dzieje się w aplikacji na telefon. Jedyne wyjścia to App Store, Google Play
 * oraz dokumenty i kontakt (wymagane przez sklepy i RODO).
 */

function StoreButtons({className = ''}) {
  return (
    <div className={`lp-stores ${className}`.trim()}>
      <a className="lp-store" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Pobierz aplikację MyBeautyPlace z App Store">
        <AppleFilled />
        <span>
          <span className="lp-store-l">Pobierz z</span>
          <span className="lp-store-n">App Store</span>
        </span>
      </a>
      <a className="lp-store" href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" aria-label="Pobierz aplikację MyBeautyPlace z Google Play">
        <AndroidFilled />
        <span>
          <span className="lp-store-l">Pobierz z</span>
          <span className="lp-store-n">Google Play</span>
        </span>
      </a>
    </div>
  );
}

function LandingPage() {
  const rootRef = useRef(null);
  const navRef = useRef(null);

  /* Jeden listener scrolla obsługuje pasek i odsłanianie sekcji.
     Stan ukrycia sekcji włącza dopiero JS (klasa lp-anim), więc bez skryptów albo
     przy zdławionym renderowaniu strona jest po prostu widoczna, zamiast zostać pusta.
     Zwykły listener zamiast IntersectionObservera: elementów jest kilka, koszt żaden,
     a mechanizm działa też tam, gdzie przeglądarka wstrzymuje klatki. */
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    let pending = Array.from(root.querySelectorAll('.lp-reveal'));
    if (pending.length) root.classList.add('lp-anim');

    const onScroll = () => {
      const nav = navRef.current;
      if (nav) nav.classList.toggle('lp-nav--scrolled', window.scrollY > 8);
      if (!pending.length) return;
      const trigger = window.innerHeight - 40;
      pending = pending.filter(el => {
        if (el.getBoundingClientRect().top > trigger) return true;
        el.classList.add('lp-reveal--in');
        return false;
      });
    };

    window.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('resize', onScroll);
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div className="lp-page" ref={rootRef}>
      {/* ── Górny pasek ── */}
      <header className="lp-nav" ref={navRef}>
        <div className="lp-nav-inner">
          <Link to="/" className="lp-nav-logo">
            <img src="/logo-icon.png" alt="" className="lp-nav-logo-img" />
            MyBeautyPlace
          </Link>
          <div className="lp-nav-actions">
            <a href={BIZ_URL} className="lp-nav-link" target="_blank" rel="noopener noreferrer">
              Dla salonów
            </a>
            <a href="#pobierz" className="lp-btn lp-btn-primary">Pobierz aplikację</a>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-bg" aria-hidden="true">
          <div className="lp-hero-blob lp-hero-blob--1" />
          <div className="lp-hero-blob lp-hero-blob--2" />
          <div className="lp-hero-grid" />
        </div>
        <div className="lp-hero-inner">
          <div className="lp-hero-copy">
            <span className="lp-hero-badge">
              <span className="lp-hero-badge-dot" />
              Darmowa aplikacja na iOS i Androida
            </span>
            <h1 className="lp-hero-h1">Wizyty, punkty i nagrody w salonach beauty. <em>W telefonie</em>.</h1>
            <p className="lp-hero-lead">
              MyBeautyPlace to aplikacja dla klientów salonów kosmetycznych i fryzjerskich. Umawiaj wizyty,
              zbieraj punkty za każdą z nich i wymieniaj je na nagrody u swojego kosmetologa.
            </p>
            <StoreButtons className="lp-stores--center" />
            <ul className="lp-hero-trust">
              <li><CheckCircleOutlined /> Za darmo</li>
              <li><CheckCircleOutlined /> Bez papierowych karteczek</li>
              <li><CheckCircleOutlined /> iOS i Android</li>
            </ul>
            <p className="lp-hero-note">
              Cała obsługa konta odbywa się w aplikacji. Ta strona jest tylko wizytówką.
            </p>
          </div>
        </div>
      </section>

      {/* ── Jak to działa ── */}
      <section className="lp-how">
        <div className="lp-wrap">
          <div className="lp-section-head lp-section-head--center lp-reveal">
            <span className="lp-eyebrow">Jak to działa</span>
            <h2 className="lp-h2">Trzy kroki do pierwszej <em>nagrody</em></h2>
            <p className="lp-subhead">Umawiasz wizytę, punkty naliczają się same, a Ty decydujesz tylko, na co je wymienić.</p>
          </div>
          <div className="lp-how-steps">
            <div className="lp-how-line" aria-hidden="true" />

            <div className="lp-how-step lp-reveal">
              <div className="lp-how-num">1</div>
              <span className="lp-how-time">w aplikacji</span>
              <h3 className="lp-how-h">Umów wizytę</h3>
              <p className="lp-how-p">Wybierz salon, usługę i wolny termin, a rezerwację potwierdzisz w kilka sekund, o każdej porze i bez dzwonienia.</p>
              <div className="lp-art" aria-hidden="true">
                <div className="lp-book">
                  <div className="lp-book-head">
                    <span className="lp-book-svc">Manicure hybrydowy</span>
                    <span className="lp-book-date">wt, 8 lip</span>
                  </div>
                  <div className="lp-book-slots">
                    <span className="lp-book-slot">10:00</span>
                    <span className="lp-book-slot lp-book-slot--on">12:30</span>
                    <span className="lp-book-slot">15:00</span>
                  </div>
                  <div className="lp-book-btn">Rezerwuj termin</div>
                </div>
              </div>
            </div>

            <div className="lp-how-step lp-reveal" style={{transitionDelay: '0.08s'}}>
              <div className="lp-how-num">2</div>
              <span className="lp-how-time">po każdej wizycie</span>
              <h3 className="lp-how-h">Zbierz punkty</h3>
              <p className="lp-how-p">Jeśli salon prowadzi program lojalnościowy, po zakończonej wizycie punkty naliczają się automatycznie. Nic nie trzeba skanować ani liczyć.</p>
              <div className="lp-art" aria-hidden="true">
                <div className="lp-receipt">
                  <div className="lp-receipt-row"><span>Manicure hybrydowy</span><span>160 zł</span></div>
                  <div className="lp-receipt-div" />
                  <div className="lp-receipt-pts"><span>Punkty za wizytę</span><b>+160</b></div>
                </div>
              </div>
            </div>

            <div className="lp-how-step lp-reveal" style={{transitionDelay: '0.16s'}}>
              <div className="lp-how-num">3</div>
              <span className="lp-how-time">jednym kliknięciem</span>
              <h3 className="lp-how-h">Odbierz nagrodę</h3>
              <p className="lp-how-p">Gdy uzbierasz wystarczająco punktów, odbierasz nagrodę w aplikacji. Salon widzi ją od razu, a Ty po prostu korzystasz z rabatu.</p>
              <div className="lp-art" aria-hidden="true">
                <div className="lp-redeem-card">
                  <div className="lp-redeem-ico">🎁</div>
                  <div className="lp-redeem-main">
                    <div className="lp-redeem-t">-20% na zabieg</div>
                    <div className="lp-redeem-s">✓ Do odebrania</div>
                  </div>
                  <div className="lp-redeem-btn">Odbierz</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pobierz aplikację ── */}
      <section className="lp-appdl" id="pobierz">
        <div className="lp-wrap lp-appdl-inner">
          <div className="lp-reveal">
            <span className="lp-eyebrow lp-eyebrow--pill">Pobierz</span>
            <h2 className="lp-h2">Cały salon zawsze <em>w kieszeni</em></h2>
            <p className="lp-feature-p">
              Pobierz darmową aplikację MyBeautyPlace i od pierwszej wizyty miej terminy, saldo punktów,
              nagrody i przypomnienia pod ręką.
            </p>
            <ul className="lp-flist">
              <li><span className="lp-fcheck" />Rezerwacja wizyt w kilka sekund</li>
              <li><span className="lp-fcheck" />Punkty i nagrody widoczne na bieżąco</li>
              <li><span className="lp-fcheck" />Przypomnienia o zbliżających się wizytach</li>
            </ul>
            <StoreButtons />
            <p className="lp-appdl-note">
              Konto zakładasz w aplikacji, korzystanie z niej jest bezpłatne.
            </p>
          </div>
          <div className="lp-appart lp-reveal">
            <div className="lp-phone" aria-hidden="true">
              <div className="lp-phone-screen">
                <div className="lp-phone-hero">
                  <div className="lp-phone-greet">Cześć 👋</div>
                  <div className="lp-phone-ball">Punkty · La Belle</div>
                  <div className="lp-phone-bal">1 240 <small>pkt</small></div>
                </div>
                <div className="lp-phone-body">
                  <div className="lp-phone-sec">Nagrody</div>
                  <div className="lp-phone-rw lp-phone-rw--ready">
                    <div className="lp-phone-rw-ico">🎁</div>
                    <div>
                      <div className="lp-phone-rw-t">-20% na zabieg</div>
                      <div className="lp-phone-rw-s">✓ Do odebrania</div>
                    </div>
                  </div>
                  <div className="lp-phone-rw">
                    <div className="lp-phone-rw-ico">💧</div>
                    <div>
                      <div className="lp-phone-rw-t">Darmowe oczyszczanie</div>
                      <div className="lp-phone-rw-s">Brakuje 260 pkt</div>
                    </div>
                  </div>
                  <div className="lp-phone-rw">
                    <div className="lp-phone-rw-ico">🧴</div>
                    <div>
                      <div className="lp-phone-rw-t">Serum w prezencie</div>
                      <div className="lp-phone-rw-s">Brakuje 760 pkt</div>
                    </div>
                  </div>
                </div>
                <div className="lp-phone-tabs">
                  <span className="lp-phone-tab">🏠</span>
                  <span className="lp-phone-tab lp-phone-tab--active">🎁</span>
                  <span className="lp-phone-tab">📅</span>
                  <span className="lp-phone-tab">👤</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stopka ── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-top">
            <div>
              <Link to="/" className="lp-footer-logo">
                <img src="/logo-icon.png" alt="" className="lp-footer-logo-img" />
                MyBeautyPlace
              </Link>
              <p className="lp-footer-tag">Wizyty, punkty i nagrody w salonach beauty, w jednej aplikacji na telefon.</p>
              <div className="lp-footer-apps">
                <a className="lp-footer-app" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                  <AppleFilled /> App Store
                </a>
                <a className="lp-footer-app" href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer">
                  <AndroidFilled /> Google Play
                </a>
              </div>
            </div>
            <div className="lp-footer-col">
              <h4 className="lp-footer-col-title">MyBeautyPlace</h4>
              <Link to="/info?tab=kontakt" className="lp-footer-link">Kontakt</Link>
              <Link to="/info?tab=faq" className="lp-footer-link">FAQ</Link>
              <Link to="/info?tab=regulamin" className="lp-footer-link">Regulamin</Link>
              <Link to="/info?tab=prywatnosc" className="lp-footer-link">Polityka prywatności</Link>
            </div>
            <div className="lp-footer-col">
              <h4 className="lp-footer-col-title">Dla salonów</h4>
              <a href={BIZ_URL} className="lp-footer-link" target="_blank" rel="noopener noreferrer">System dla salonu</a>
              <a href={`${BIZ_URL}/pakiety.html`} className="lp-footer-link" target="_blank" rel="noopener noreferrer">Cennik</a>
            </div>
          </div>
          <div className="lp-footer-bottom">
            &copy; 2026 MyBeautyPlace &middot; GRUPA POMARAŃSKI I SOBOTA Sp. z o.o., ul. Komuny Paryskiej 58A/52, 30-389 Kraków &middot; KRS 0001238833 &middot; NIP 6762719106
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
